import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ArrowRight, Clock, Users, Quote, Check } from "lucide-react";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { caseStudies } from "@/data/caseStudies";
import SEOHead from "@/components/SEOHead";

export default function CaseStudyPage() {
  const { slug } = useParams();
  const study = caseStudies.find((c) => c.slug === slug);

  if (!study) return <Navigate to="/work" replace />;

  return (
    <main data-testid={`case-study-${slug}`}>
      <SEOHead
        title={`${study.title} — Venury Case Study`}
        description={study.shortDesc}
        url={`https://venury.tech/work/${slug}`}
        type="article"
      />
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={study.heroImage} alt={study.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1116] via-[#0B1116]/60 to-[#0B1116]/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-16 pt-32 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
            <Link to="/work" data-testid="back-to-work" className="inline-flex items-center gap-2 font-mono text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200">
              <ArrowLeft size={14} /> All Case Studies
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex gap-2 mb-4">
            <span className="font-mono text-[10px] uppercase tracking-wider bg-teal-primary/20 text-teal-bright px-3 py-1 rounded">
              {study.category}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-wider bg-white/10 text-slate-400 px-3 py-1 rounded">
              {study.tag}
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight max-w-4xl leading-[1.1]"
          >
            {study.title}
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-wrap gap-6 mt-8"
          >
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Clock size={14} className="text-teal-bright" /> {study.timeline}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Users size={14} className="text-teal-bright" /> {study.teamSize}
            </div>
          </motion.div>

          {/* Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex gap-8 md:gap-12 mt-10 pt-8 border-t border-white/10"
          >
            {study.metrics.map((m, i) => (
              <div key={i}>
                <span className="font-mono text-3xl md:text-4xl font-bold text-teal-bright block">{m.value}</span>
                <span className="font-mono text-xs text-slate-600 uppercase tracking-wider">{m.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ==================== CAPABILITY DEMO DISCLAIMER ==================== */}
      <section className="pt-6 pb-0">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="rounded-lg border border-orange-bright/20 bg-orange-bright/5 px-5 py-3">
            <p className="text-xs text-slate-400 leading-relaxed">
              <span className="text-orange-bright font-semibold">Capability Demo:</span>{" "}
              This case study demonstrates our technical capabilities and the types of systems we build. Details represent realistic project scenarios based on our engineering expertise.
            </p>
          </div>
        </div>
      </section>

      {/* Challenge */}
      <section data-testid="challenge-section" className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-orange-bright uppercase tracking-widest block mb-4">The Challenge</span>
            <div className="space-y-6">
              {study.challenge.map((p, i) => (
                <p key={i} className="text-base md:text-lg text-slate-400 leading-relaxed">{p}</p>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Solution */}
      <section data-testid="solution-section" className="py-20 md:py-28 bg-[#080C10]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Our Solution</span>
            <div className="space-y-6">
              {study.solution.map((p, i) => (
                <p key={i} className="text-base md:text-lg text-slate-400 leading-relaxed">{p}</p>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Results */}
      <section data-testid="results-section" className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Results</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-10">
              Measurable Impact
            </h2>
          </ScrollReveal>

          <StaggerContainer className="space-y-4" staggerDelay={0.08}>
            {study.results.map((result, i) => (
              <StaggerItem key={i}>
                <div className="flex items-start gap-4 p-4 rounded-lg border border-white/5 hover:border-teal-primary/20 transition-colors duration-300">
                  <div className="w-6 h-6 rounded-full bg-teal-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={12} className="text-teal-bright" />
                  </div>
                  <span className="text-base text-slate-400">{result}</span>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Testimonial */}
      {study.testimonial && (
        <section data-testid="case-testimonial" className="py-20 md:py-28 bg-[#080C10]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <ScrollReveal>
              <div className="glass-card rounded-lg p-8 md:p-12">
                <Quote size={32} className="text-teal-primary/30 mb-6" />
                <p className="text-lg md:text-xl text-white leading-relaxed italic mb-8">
                  "{study.testimonial.quote}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-teal-primary/20 flex items-center justify-center">
                    <span className="text-teal-bright font-heading font-bold">{study.testimonial.author[0]}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">{study.testimonial.author}</p>
                    <p className="text-sm text-slate-600">{study.testimonial.role}</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Tech Stack */}
      <section data-testid="case-tech-stack" className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-orange-bright uppercase tracking-widest block mb-4">Technology</span>
            <h2 className="font-heading text-3xl font-bold text-white tracking-tight mb-8">Tech Stack</h2>
            <div className="flex flex-wrap gap-3">
              {study.techStack.map((tech) => (
                <span key={tech} className="font-mono text-sm px-4 py-2 rounded-lg bg-navy-base/50 border border-white/5 text-slate-400 hover:text-teal-bright hover:border-teal-primary/30 transition-colors duration-200">
                  {tech}
                </span>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Next Case Study */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            <Link
              to="/work"
              data-testid="all-case-studies-link"
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200"
            >
              <ArrowLeft size={14} /> All Case Studies
            </Link>
            <Link
              to={`/work/${study.nextSlug}`}
              data-testid="next-case-study-link"
              className="inline-flex items-center gap-3 group"
            >
              <div className="text-right">
                <span className="font-mono text-[10px] text-slate-600 uppercase tracking-widest block">Next Project</span>
                <span className="text-sm text-white group-hover:text-teal-bright transition-colors duration-200">{study.nextTitle}</span>
              </div>
              <ArrowRight size={16} className="text-slate-600 group-hover:text-teal-bright transition-colors duration-200" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
